import React, { useState, useEffect } from "react";
import { Filter, Layers, RefreshCw, AlertCircle, CheckCircle2, Server, Clock, AlertTriangle } from "lucide-react";
import { fetchJobQueueTelemetry, JobTelemetryEntry, JobTelemetrySummary } from "./settingsApi";

type JobFilter = "All" | "Running" | "Pending" | "Completed" | "Failed";

const statusStyle: Record<JobTelemetryEntry["status"], string> = {
  Running:   "bg-indigo-950 text-indigo-300 border-indigo-800",
  Pending:   "bg-amber-950 text-amber-300 border-amber-800",
  Completed: "bg-emerald-950 text-emerald-300 border-emerald-800",
  Failed:    "bg-rose-950 text-rose-300 border-rose-800",
  Cancelled: "bg-slate-800 text-slate-400 border-slate-700",
};

const statusDot: Record<JobTelemetryEntry["status"], string> = {
  Running:   "bg-indigo-400 animate-pulse",
  Pending:   "bg-amber-400",
  Completed: "bg-emerald-400",
  Failed:    "bg-rose-500",
  Cancelled: "bg-slate-600",
};

const FILTERS: JobFilter[] = ["All", "Running", "Pending", "Completed", "Failed"];

export const JobQueuePage: React.FC = () => {
  const [filter, setFilter] = useState<JobFilter>("All");
  const [jobs, setJobs] = useState<JobTelemetryEntry[]>([]);
  const [summary, setSummary] = useState<JobTelemetrySummary>({
    running: 0,
    pending: 0,
    completed: 0,
    failed: 0,
    total: 0,
    worker_status: "Checking...",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadJobs(true);
    const timer = setInterval(() => loadJobs(false), 10000);
    return () => clearInterval(timer);
  }, []);

  const loadJobs = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setError("");

    try {
      const data = await fetchJobQueueTelemetry();
      setJobs(data.jobs || []);
      if (data.summary) {
        setSummary(data.summary);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to fetch background job telemetry.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filteredJobs = filter === "All"
    ? jobs
    : jobs.filter((j) => j.status === filter);

  const counts: Record<JobFilter, number> = {
    All: summary.total,
    Running: summary.running,
    Pending: summary.pending,
    Completed: summary.completed,
    Failed: summary.failed,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-cyan-400" />
        <span>Fetching background job queue telemetry...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-xs font-sans">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-900/80 border border-slate-800 rounded-xl">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-cyan-400" />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-slate-100">Celery Job Queue & Telemetry Monitor</h2>
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                summary.worker_status === "Online"
                  ? "bg-emerald-950 text-emerald-400 border border-emerald-800"
                  : "bg-slate-800 text-slate-400 border border-slate-700"
              }`}>
                Worker Engine: {summary.worker_status}
              </span>
            </div>
            <p className="text-[10px] text-slate-500">Track active Celery worker tasks and system-wide background operations.</p>
          </div>
        </div>
        <button
          onClick={() => loadJobs(false)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-200 rounded-lg transition-colors text-[11px]"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-cyan-400" : ""}`} />
          <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "Running",   val: counts.Running,   color: "text-indigo-400" },
          { label: "Pending",   val: counts.Pending,   color: "text-amber-400" },
          { label: "Completed", val: counts.Completed, color: "text-emerald-400" },
          { label: "Failed",    val: counts.Failed,    color: "text-rose-400" },
        ].map((s) => (
          <div key={s.label} className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
            <p className={`text-xl font-bold font-mono ${s.color}`}>{s.val}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1.5 p-1.5 bg-slate-900 border border-slate-800 rounded-xl">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-all ${
              filter === f ? "bg-indigo-600 text-white font-bold" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {f} ({counts[f]})
          </button>
        ))}
      </div>

      {/* Job List */}
      {filteredJobs.length === 0 ? (
        <div className="p-8 text-center bg-slate-950 border border-slate-800 rounded-xl text-slate-400">
          <Layers className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="font-semibold text-xs text-slate-300">No Jobs Found</p>
          <p className="text-[11px] text-slate-500 mt-1">No background jobs match the selected filter standard.</p>
        </div>
      ) : (
        <div className="space-y-2 font-mono">
          {filteredJobs.map((job) => (
            <div key={job.id} className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl hover:border-slate-700 transition-colors space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusDot[job.status]}`} />
                  <span className="text-[11px] font-bold text-slate-100 font-sans truncate">{job.name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {job.duration && <span className="text-[10px] text-slate-500">{job.duration}</span>}
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase ${statusStyle[job.status]}`}>
                    {job.status}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 text-[10px] text-slate-400">
                <span>Queue: <code className="text-cyan-400">{job.queue}</code></span>
                <span>·</span>
                <span>Worker: <code className="text-indigo-400">{job.worker}</code></span>
                {job.started_at && (
                  <>
                    <span>·</span>
                    <span>Started: {new Date(job.started_at).toLocaleTimeString()}</span>
                  </>
                )}
                {job.retries > 0 && (
                  <>
                    <span>·</span>
                    <span className="text-rose-400 font-bold">{job.retries}x retried</span>
                  </>
                )}
                {job.telemetry_source && (
                  <span className="ml-auto text-[9px] text-slate-600 bg-slate-900 border border-slate-800 rounded px-1.5 py-0.5 font-sans">
                    {job.telemetry_source}
                  </span>
                )}
              </div>

              {job.error_message && (
                <div className="p-2 bg-rose-950/40 border border-rose-900/60 rounded text-rose-300 text-[10px] font-sans flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />
                  <span className="truncate">{job.error_message}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
